import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface Product {
  title: string;
  editionLabel: string;
  priceText: string;
  thumbnailUrl: string;
  weight: number;
}
const PRODUCTS: Product[] = [{
  title: 'Protocolo de 72 Horas',
  editionLabel: 'Pack Completo + 3 Bonos',
  priceText: '$8.999',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/fCqSNXB1Kex3m9afbe5REW/bono_01.png',
  weight: 60
}, {
  title: 'Protocolo + Scanner Limpieza',
  editionLabel: 'Pack + Order Bump',
  priceText: '$14.999',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/f4Mk4cvL556mjqGQ7Cr1gb/ORDER_BUMP_01.png',
  weight: 25
}, {
  title: 'Protocolo + Calendario + Scanner',
  editionLabel: 'Pack Completo Total',
  priceText: '$20.999',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/xjbYaLUMozNrPhxSXb4rRo/order_bump_02.png',
  weight: 15
}];
const NAMES = ['María', 'Lucía', 'Valentina', 'Camila', 'Sofía', 'Martina', 'Catalina', 'Florencia', 'Julieta', 'Agustina', 'Carolina', 'Paula', 'Daniela', 'Romina', 'Natalia', 'Andrea', 'Gabriela', 'Laura', 'Mariana', 'Victoria', 'Juan', 'Matías', 'Nicolás', 'Santiago', 'Sebastián', 'Tomás', 'Lucas', 'Facundo', 'Martín', 'Federico', 'Diego', 'Gonzalo', 'Pablo', 'Alejandro', 'Marcos', 'Rodrigo', 'Hernán', 'Leandro', 'Maximiliano', 'Ezequiel'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Bahía Blanca', 'Paraná', 'San Luis', 'Corrientes', 'San Miguel de Tucumán', 'Quilmes', 'Lanús', 'Vicente López', 'Tigre', 'Pilar', 'San Isidro', 'Morón'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeightedProduct(products: Product[]): Product {
  const totalWeight = products.reduce((sum, p) => sum + p.weight, 0);
  let r = Math.random() * totalWeight;
  for (const p of products) {
    r -= p.weight;
    if (r <= 0) return p;
  }
  return products[0];
}
function randomInterval(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: Product;
}
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const recentNames = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pickName = useCallback(() => {
    const available = NAMES.filter(n => !recentNames.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    recentNames.current.push(name);
    if (recentNames.current.length > 10) {
      recentNames.current.shift();
    }
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const name = pickName();
    const city = pickRandom(CITIES);
    const product = pickWeightedProduct(PRODUCTS);
    idCounter.current += 1;
    setNotification({
      id: idCounter.current,
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    // Schedule next notification
    const nextDelay = randomInterval(8000, 15000);
    timeoutRef.current = setTimeout(showNotification, nextDelay + 600);
  }, [pickName]);
  useEffect(() => {
    // First notification after 3 seconds
    timeoutRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showNotification]);
  const variants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none" data-id="element-394">
      <AnimatePresence mode="wait" data-id="element-395">
        {visible && notification && <motion.div key={notification.id} initial={variants.initial} animate={variants.animate} exit={variants.exit} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-xl border border-[#1C1C1C]/10 px-4 py-3 flex items-center gap-3 max-w-sm w-[calc(100vw-2rem)]" data-id="element-396">
            <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-397" />
            <div className="min-w-0 flex-1" data-id="element-398">
              <p className="text-sm text-[#1C1C1C] leading-snug" data-id="element-399">
                <strong className="font-semibold" data-id="element-400">{notification.name}</strong>{' '}
                en{' '}
                <span className="text-[#1C1C1C]/70" data-id="element-401">{notification.city}</span>{' '}
                compró
              </p>
              <p className="text-xs text-[#2D8C5A] font-semibold truncate mt-0.5" data-id="element-402">
                {notification.product.editionLabel} —{' '}
                {notification.product.priceText}
              </p>
            </div>
            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#2D8C5A] animate-pulse" data-id="element-403" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}